import { FuelIcon } from "lucide-react";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import {
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { Badge } from "@/components/catalyst/badge";
import { useTranslations } from "use-intl";
import { bookingStatusColorMap } from "@/app/Bookings/Utils/bookingStatusColorMap";
import { useBookingActions } from "@/app/Bookings/Hooks/useBookingActions";
import { useBookingRecord } from "@/app/Bookings/Hooks/useBookingRecord";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { bookingTaskCountQuery } from "@/app/Bookings/GraphQL/bookingTaskCountQuery";
import {
  ModelType,
  QueryTasksWhereColumn,
  RelocationType,
  TaskStatus,
} from "@/gql/graphql";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "@/components/catalyst/link";

export function BookingRecordScreen() {
  const { isAdmin } = useTenant();
  const booking = useBookingRecord();
  const actions = useBookingActions()(booking);
  const isGapRental = booking.relocation.type === RelocationType.GapRental;

  const t = useTranslations("booking");

  const { data: taskCountData } = useGqlQuery(bookingTaskCountQuery, {
    status: [TaskStatus.Pending, TaskStatus.Overdue],
    where: {
      AND: [
        {
          column: QueryTasksWhereColumn.RelatedToId,
          value: booking.id,
        },
        {
          column: QueryTasksWhereColumn.RelatedToType,
          value: ModelType.Booking,
        },
      ],
    },
  });
  const openTaskCount = taskCountData?.tasks.paginatorInfo.total ?? 0;

  const listingPreviewUrl = new URL(booking.relocation.customer_url);
  listingPreviewUrl.searchParams.set("preview", "true");

  return (
    <RecordScreen
      tabParams={{ bookingId: booking.id }}
      title={booking.reference}
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/$tenant/$orgId/supplier/{-$supplierId}/bookings"
                params={(prev) => prev}
              >
                Bookings
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{booking.reference}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      actionsGroups={actions}
      badges={
        <>
          <Badge
            color={bookingStatusColorMap[booking.status]}
            className="capitalize"
          >
            {t(`status.${booking.status}`)}
          </Badge>

          {booking.relocationLine?.reference ? (
            <Badge color="zinc">
              Plate: {booking.relocationLine.reference}
            </Badge>
          ) : null}

          {booking.supplier_reference ? (
            <Badge color="zinc">
              <SupplierIcon
                model={booking.supplier}
                className="size-3.5 shrink-0 rounded-sm"
              />
              <Link
                className="underline"
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/settings"
                params={(prev) => prev}
                search={{ supplier: booking.supplier.id }}
              >
                {booking.supplier.name}
              </Link>{" "}
              reference: {booking.supplier_reference}
            </Badge>
          ) : (
            <Badge color="zinc">
              <SupplierIcon
                model={booking.supplier}
                className="size-3.5 shrink-0 rounded-sm"
              />
              <Link
                className="underline"
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/settings"
                params={(prev) => prev}
                search={{ supplier: booking.supplier.id }}
              >
                {booking.supplier.name}
              </Link>
            </Badge>
          )}

          <Badge>
            <Link
              className="underline"
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to={
                isGapRental
                  ? "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId"
                  : "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
              }
              params={(prev) => ({
                ...prev,
                relocationId: booking.relocation.id,
              })}
            >
              {booking.relocation.reference}
            </Link>
          </Badge>

          <Badge>
            <a
              href={listingPreviewUrl.toString()}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              View listing{" "}
              <ArrowTopRightOnSquareIcon className="inline size-3.5" />
            </a>
          </Badge>
        </>
      }
      tabs={[
        {
          name: "Details",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId",
          Icon: DocumentTextIcon,
        },
        {
          name: "Tasks",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId/tasks",
          Icon: ClockIcon,
          notificationCount: openTaskCount,
        },

        {
          name: "Find relocation",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId/find-relocation",
          Icon: MagnifyingGlassIcon,
          isHidden: !isAdmin,
          isAdminTab: true,
        },
        {
          name: "Payments",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId/payments",
          Icon: CurrencyDollarIcon,
          isHidden: !isAdmin,
          isAdminTab: true,
        },
        {
          name: "Fuel / ferry refunds",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId/receipts",
          Icon: FuelIcon,
          isHidden: !isAdmin && booking.supplier.imoova_refunds_fuel,
          isAdminTab: true,
        },
      ]}
    />
  );
}
